// DESCRIPTION: Event card component for grid layout. Uses full overlay approach —
// title and location overlaid on image with gradient scrim, metadata row below.

import { __ } from '@wordpress/i18n';
import { getEventStyleVars } from '../utils/eventStyling';

const EventCard = ( { event } ) => {
	const eventUrl = `${ window.location.origin }/events/${ event.slug }/`;

	const typeLabels = {
		'one-on-one': __( '1:1', 'nemtly-booking' ),
		group: __( 'Group', 'nemtly-booking' ),
		event: __( 'Event', 'nemtly-booking' ),
	};

	const formatPrice = () => {
		if ( ! event.price || event.price === 0 ) {
			return __( 'Free', 'nemtly-booking' );
		}

		return `${ event.currency_symbol }${ parseFloat( event.price ).toFixed(
			2
		) }`;
	};

	return (
		<a
			href={ eventUrl }
			className="nb-group nb-event-card nb-transition-all nb-duration-300"
			style={ getEventStyleVars( event ) }
			data-event-id={ event.id }
		>
			{ /* Image area with overlay */ }
			<div className="nb-relative nb-h-56 nb-overflow-hidden">
				{ event.image_url ? (
					<img
						src={ event.image_url }
						alt={ event.title }
						className="nb-w-full nb-h-full nb-object-cover group-hover:nb-scale-105 nb-transition-transform nb-duration-300"
						loading="lazy"
					/>
				) : (
					<div className="nb-w-full nb-h-full nb-bg-gradient-to-br nb-from-slate-100 nb-to-slate-200 nb-flex nb-items-center nb-justify-center">
						<div className="nb-text-center">
							<div className="nb-w-14 nb-h-14 nb-mx-auto nb-mb-3 nb-rounded-xl nb-bg-white/80 nb-flex nb-items-center nb-justify-center nb-shadow-sm">
								<svg
									className="nb-w-7 nb-h-7 nb-text-slate-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={ 1.5 }
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<p className="nb-text-sm nb-font-medium nb-text-slate-400">
								{ event.date ||
									__( 'Event', 'nemtly-booking' ) }
							</p>
						</div>
					</div>
				) }

				{ /* Gradient scrim */ }
				<div className="nb-absolute nb-inset-0 nb-bg-gradient-to-t nb-from-black/70 nb-via-black/20 nb-to-transparent" />

				{ /* Event type pill */ }
				{ event.event_type && (
					<span className="nb-absolute nb-top-3 nb-left-3 nb-px-2.5 nb-py-1 nb-rounded-md nb-text-xs nb-font-medium nb-bg-white/90 nb-text-gray-800 nb-backdrop-blur-sm">
						{ typeLabels[ event.event_type ] || event.event_type }
					</span>
				) }

				{ /* Title + Location overlay */ }
				<div className="nb-absolute nb-bottom-0 nb-left-0 nb-right-0 nb-p-4">
					<h3 className="nb-text-base nb-font-semibold nb-text-white nb-leading-snug nb-line-clamp-2 nb-mb-1">
						{ event.title }
					</h3>
					{ event.location && (
						<p className="nb-text-sm nb-text-white/80 nb-line-clamp-1">
							{ event.location }
						</p>
					) }
				</div>
			</div>

			{ /* Metadata row */ }
			<div className="nb-flex nb-items-center nb-justify-between nb-px-4 nb-py-3 nb-text-sm nb-text-gray-600">
				<div className="nb-flex nb-items-center nb-gap-4">
					{ /* Date & Time — hidden for slot-based event types */ }
					{ event.event_type !== 'one-on-one' &&
						event.event_type !== 'group' && (
							<>
								<span className="nb-inline-flex nb-items-center nb-gap-1.5">
									<svg
										className="nb-w-4 nb-h-4 nb-text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={ 2 }
											d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
									{ event.date }
								</span>

								{ event.time && (
									<span className="nb-inline-flex nb-items-center nb-gap-1.5">
										<svg
											className="nb-w-4 nb-h-4 nb-text-gray-400"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={ 2 }
												d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										{ event.time }
									</span>
								) }
							</>
						) }
				</div>

				{ /* Price */ }
				<span className="nb-inline-flex nb-items-center nb-gap-1.5 nb-font-medium">
					<svg
						className="nb-w-4 nb-h-4 nb-text-gray-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={ 2 }
							d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
						/>
					</svg>
					{ formatPrice() }
				</span>
			</div>
		</a>
	);
};

export default EventCard;
